import React, { useRef, useState, useEffect } from 'react'
import useSize from './useSize'
import { scaleLinear } from 'd3-scale'
import { config, formattedDate, getDateOfISOWeek } from './config'
import './styles.scss'

type ChartProps = {
  data: weektype[]
  scalemax?: number
  rechtheightmulti?: number
  inverted?: boolean
  absolute?: boolean
}

const getCorrectTextColor = (rgbstring: string) => {
  const threshold = 130
  const getRGB = (str: string) => {
    var match = str.match(
      /rgba?\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)?(?:, ?(\d(?:\.\d?))\))?/
    )
    return match
      ? {
          r: +match[1],
          g: +match[2],
          b: +match[3]
        }
      : { r: 0, g: 0, b: 0 }
  }
  let rgb = getRGB(rgbstring)
  let cBrightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
  if (cBrightness > threshold) {
    return '#000000'
  } else {
    return '#ffffff'
  }
}

export const Chart: React.FC<ChartProps> = ({
  data,
  scalemax = 200,
  rechtheightmulti = 1,
  inverted = false,
  absolute = false
}) => {
  const [container, size] = useSize()
  const [ttdata, setttdata] = useState<[number[], number] | undefined>()
  const [fontheight, setfontheight] = useState<number>(20)
  const testtext = useRef<SVGTextElement>(null)
  const svg = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if (!testtext) return
    setfontheight(testtext.current?.getBBox().height || 20)
  }, [testtext.current])

  const width = size.width
  const gutterTop = 60
  const gutterRight = size.isMobile ? 15 : 17
  const gutterLeft = 50
  const rectwidth = Math.floor((width - gutterLeft - gutterRight) / data.length)
  const rectheight = 20 * rechtheightmulti
  const height = rectheight * (config.ages.length + 1) + gutterTop

  const color = React.useMemo(
    () =>
      scaleLinear<string, number>()
        .domain([0, scalemax / 2, scalemax])
        .range(inverted ? config.colorrange_reversed : config.colorrange),
    [size, scalemax, inverted, absolute]
  )

  return (
    <div className='chart'>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        <svg
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
        >
          <defs>
            <linearGradient id='gradient' x1='0%' y1='0%' x2='100%' y2='0%'>
              {color.range().map((col, i) => {
                let step = 100 / (config.colorrange.length - 1)
                return <stop offset={i * step + '%'} stopColor={col} />
              })}
            </linearGradient>
            <filter id='shadow'>
              <feDropShadow
                dx='0'
                dy='0'
                stdDeviation='2'
                floodColor='#202346'
                floodOpacity='0.5'
              />
            </filter>
          </defs>
          <text className='title' x={gutterLeft} y={24}>
            {config.cantonnames[data[0].canton]}
          </text>
          <text ref={testtext} y={-20}>
            a
          </text>
          {data.map((d, i) => {
            return (
              <g
                transform={
                  'translate(' +
                  (gutterLeft + i * rectwidth) +
                  ',' +
                  gutterTop +
                  ')'
                }
              >
                {config.ages.map((age, j) => {
                  let prop = age + (absolute ? '_abs' : '')

                  let col = color(d[prop] as number).toString()
                  let y = j * rectheight

                  return (
                    <g transform={'translate(' + 0 + ',' + y + ')'}>
                      <rect
                        pointerEvents={
                          rectwidth > 60 && rectheight > 30 ? 'none' : 'all'
                        }
                        className={'data ' + prop}
                        data-val={d[prop]}
                        data-pos={i + '/' + j}
                        width={rectwidth}
                        height={rectheight}
                        fill={col}
                        stroke={col}
                        onMouseMove={(e: React.MouseEvent<SVGRectElement>) => {
                          let target = e.target as SVGRectElement
                          let pos = [0, 0]
                          let val = 0
                          if (!target.classList.contains('data')) return

                          if (target.dataset.pos) {
                            pos = target.dataset.pos.split('/').map(Number)
                          }
                          if (target.dataset.val) {
                            val = +target.dataset.val
                          }
                          setttdata([pos, val])
                        }}
                        onMouseLeave={() => setttdata(undefined)}
                      />
                      {rectwidth > 60 && rectheight > 30 && (
                        <text
                          className='value'
                          x={rectwidth / 2}
                          y={rectheight - 12}
                          textAnchor='middle'
                          fill={getCorrectTextColor(color(d[prop]))}
                        >
                          {d[prop]}
                        </text>
                      )}
                    </g>
                  )
                })}
              </g>
            )
          })}
          {data.map((d, i) => {
            let weekofmonth = getDateOfISOWeek(+d.weeknr.toString().substr(4), +d.weeknr.toString().substr(0,4))
            let x = gutterLeft + i * rectwidth
            let date = formattedDate(weekofmonth)

            if (weekofmonth.getDate() > 7) {
              return null
            }
            return (
              <g
                transform={'translate(0,' + (gutterTop + rectheight * 9) + ')'}
              >
                <text className='tick' x={x + 4} y={20}>
                  {date}
                </text>
                <line x1={x} x2={x} y1={20} y2={0} className='tick' />
              </g>
            )
          })}
          {config.ages.map((age, i) => {
            let y = i * rectheight
            return (
              <g className='' transform={'translate(0,' + gutterTop + ')'}>
                <text
                  key={i}
                  className='tick'
                  x={gutterLeft - 10}
                  y={(i + 0.5) * rectheight + fontheight * 0.25}
                  textAnchor='end'
                >
                  {config.ageclasses[age]}
                </text>
                <line
                  x1={gutterLeft}
                  x2={width - gutterRight}
                  y1={y}
                  y2={y}
                  className='separator'
                />
              </g>
            )
          })}
          <ColorKey
            x={gutterLeft - 10}
            y={44}
            domain={[color.domain()[0], color.domain()[2]]}
            width={
              size.isMobile
                ? width - gutterLeft - gutterRight - 2 * rectwidth - 10
                : 200
            }
          />
          {ttdata && rectwidth <= 59 && (
            <Tooltip
              pos={[
                gutterLeft + ttdata[0][0] * rectwidth - (60 - rectwidth) / 2,
                gutterTop + ttdata[0][1] * rectheight
              ]}
              precision={absolute ? 0 : 1}
              width={60}
              height={22}
              val={ttdata[1]}
              anchor={
                gutterLeft + ttdata[0][0] * rectwidth >
                width - gutterRight - 2 * rectwidth
                  ? 'end'
                  : gutterLeft + ttdata[0][0] * rectwidth <
                    gutterLeft + rectwidth
                  ? 'start'
                  : 'middle'
              }
            />
          )}
        </svg>
      </div>
    </div>
  )
}

const Tooltip = ({
  val,
  pos,
  width,
  height,
  anchor = 'middle',
  precision = 1
}: {
  val: number
  pos: [number, number]
  width: number
  height: number
  anchor?: 'start' | 'middle' | 'end'
  precision?: 0 | 1
}) => {
  const polygons = {
    middle: [
      width + ',' + -height * 0.25,
      width * 0.75 + ',' + -height * 0.25,
      width * 0.5 + ',10 ',
      width * 0.25 + ',' + -height * 0.25,
      0 + ',' + -height * 0.25,
      0 + ',' + -height * 1.5,
      width + ',' + -height * 1.5
    ],
    start: [
      width * 1.5 + ',' + -height * 0.25,
      width * 0.75 + ',' + -height * 0.25,
      width * 0.5 + ',10 ',
      width * 0.5 + ',' + -height * 0.25,
      width * 0.5 + ',' + -height * 0.25,
      width * 0.5 + ',' + -height * 1.5,
      width * 1.5 + ',' + -height * 1.5
    ],
    end: [
      width * 0.5 + ',' + -height * 0.25,
      width * 0.5 + ',' + -height * 0.25,
      width * 0.5 + ',10 ',
      width * 0.25 + ',' + -height * 0.25,
      -width * 0.5 + ',' + -height * 0.25,
      -width * 0.5 + ',' + -height * 1.5,
      width * 0.5 + ',' + -height * 1.5
    ]
  }
  const textpos = { middle: width * 0.5, start: width, end: 0 }
  return (
    <g transform={'translate(' + pos[0] + ',' + pos[1] + ')'}>
      <polygon
        points={polygons[anchor].join(' ')}
        fill='#fff'
        filter='url(#shadow)'
      />
      <text
        x={textpos[anchor]}
        y={-height * 0.65}
        textAnchor='middle'
        pointerEvents='none'
        className='val'
      >
        {val.toFixed(precision)}
      </text>
    </g>
  )
}

const ColorKey = ({
  x,
  y,
  width,
  domain
}: {
  x: number
  y: number
  width: number
  domain: [number, number]
}) => (
  <g className='key' transform={'translate(' + x + ',' + y + ')'}>
    <text className='tick' x={width + 16} y={6} textAnchor='start'>
      {domain[1]}
    </text>
    <text className='tick' x={0} y={6} textAnchor='end'>
      {domain[0]}
    </text>
    <rect
      className='gradient'
      x={10}
      width={width}
      height={6}
      fill='url(#gradient)'
    />
  </g>
)
